import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PATTERN_TYPES = ["regex", "luhn", "keyword", "email", "ipv4", "iban", "ssn"];
const ACTIONS = ["log", "alert", "block"];
const empty = {
  name: "", pattern_type: "regex", pattern: "",
  min_confidence: 0.7, action: "log",
  applies_to_labels: [], enabled: true, description: "",
};

export default function DLPRules() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([api.get("/dlp/rules"), api.get("/labels")]);
      setItems(r.data); setLabels(l.data);
    } catch (err) { console.error("dlp load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/dlp/rules", form); toast.success("Rule created"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this DLP rule?")) return;
    try { await api.delete(`/dlp/rules/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleLabel = (id) => {
    const has = form.applies_to_labels.includes(id);
    setForm({ ...form, applies_to_labels: has ? form.applies_to_labels.filter((x) => x !== id) : [...form.applies_to_labels, id] });
  };

  return (
    <Layout title="DLP Rules" subtitle="DATA LOSS PREVENTION">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="dlp-count">
          {items.length} RULES · {items.filter((i) => i.enabled).length} ENABLED · {items.filter((i) => i.action === "block").length} BLOCKING
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-dlp-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
              <Plus className="w-4 h-4" /> NEW RULE
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><FileSearch className="w-4 h-4 text-cyan-400" /> Create DLP Rule</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="dlp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Pattern type</L>
                <Sel value={form.pattern_type} onChange={(v) => setForm({ ...form, pattern_type: v })} options={PATTERN_TYPES} testid="dlp-ptype-select" />
              </div>
              <div><L>Action</L>
                <Sel value={form.action} onChange={(v) => setForm({ ...form, action: v })} options={ACTIONS} testid="dlp-action-select" />
              </div>
              <div className="col-span-2"><L>Pattern</L><Input data-testid="dlp-pattern-input" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder={form.pattern_type === "regex" ? "\\b\\d{3}-\\d{2}-\\d{4}\\b" : "(optional for built-in patterns)"} /></div>
              <div><L>Min confidence (0–1)</L><Input data-testid="dlp-conf-input" type="number" step="0.05" min="0" max="1" value={form.min_confidence} onChange={(e) => setForm({ ...form, min_confidence: parseFloat(e.target.value || "0") })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Enabled</span>
                <Switch data-testid="dlp-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
              <div className="col-span-2">
                <L>Applies to labels</L>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {labels.length === 0 && <span className="text-xs text-zinc-500 font-mono">no labels defined</span>}
                  {labels.map((l) => {
                    const on = form.applies_to_labels.includes(l.label_id);
                    return (
                      <button key={l.label_id} type="button" onClick={() => toggleLabel(l.label_id)}
                        className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (on ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-zinc-700 text-zinc-500 hover:text-zinc-300")}>
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2"><L>Description</L><Input data-testid="dlp-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-dlp-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dlp-list">
        {items.map((r) => (
          <div key={r.rule_id} data-testid={`dlp-card-${r.rule_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-lg font-bold tracking-tight">{r.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{r.rule_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " +
                (r.action === "block" ? "text-red-400 border-red-500/40 bg-red-500/10"
                  : r.action === "alert" ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                  : "text-cyan-400 border-cyan-500/40 bg-cyan-500/10")}>{r.action}</span>
            </div>
            <div className="font-mono text-xs space-y-1.5 text-zinc-300">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">TYPE</span><span className="text-cyan-400">{r.pattern_type}</span></div>
              {r.pattern && <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">PATTERN</span><span className="text-zinc-400 whitespace-normal break-words inline-block max-w-[14rem] align-middle">{r.pattern}</span></div>}
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">CONF</span><span className="text-amber-400">{r.min_confidence}</span></div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">LABELS</span>{r.applies_to_labels?.length ? r.applies_to_labels.length + " bound" : "any"}</div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className={"text-[10px] font-mono uppercase tracking-widest " + (r.enabled ? "text-emerald-400" : "text-zinc-500")}>{r.enabled ? "ENABLED" : "DISABLED"}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-dlp-${r.rule_id}`} onClick={() => del(r.rule_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no DLP rules yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {options.map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
